<script setup lang="ts">
// What an encode sends, or a destination at its delay (DetailPanel): the
// node decodes the stream only while it is shown, live (MJPEG, a full
// decode) or its key frames for a still every 2 s (next to nothing).
import { computed } from 'vue'
import { api } from '../../api'
import { pageHidden, previewLive, vStream } from '../../preview'
import { useNode } from '../../store'
import PreviewToggle from './PreviewToggle.vue'

const props = defineProps<{ kind: 'encodes' | 'destinations'; id: string; delay?: number }>()
const node = useNode()
const thumbTick = computed(() => Math.floor(node.now / 2000))
const openFrame = () => window.open(api.encodeFramePng(props.id), '_blank')
const caption = computed(() =>
    props.kind === 'encodes'
        ? 'what it sends, now'
        : props.delay
          ? `what it sends, ${props.delay} s behind live`
          : 'what it sends, live (no delay)',
)
</script>

<template>
    <div class="mt-3 grid grid-cols-2 gap-3">
        <figure class="m-0 min-w-0">
            <img
                v-if="previewLive && !pageHidden"
                :key="'live-' + kind + id + (delay ?? 0)"
                v-stream
                class="block aspect-video w-full rounded bg-bg object-contain"
                :src="api.streamPreview(kind, id)"
                alt=""
                @error="($event.target as HTMLImageElement).style.visibility = 'hidden'"
            />
            <img
                v-else
                class="block aspect-video w-full rounded bg-bg object-contain"
                :src="api.streamThumb(kind, id, thumbTick)"
                alt=""
                @error="($event.target as HTMLImageElement).style.visibility = 'hidden'"
                @load="($event.target as HTMLImageElement).style.visibility = 'visible'"
            />
            <figcaption class="mt-0.5 text-xs text-fg-3">{{ caption }}</figcaption>
        </figure>
        <!-- what the choice costs is the toggle's tip, not a paragraph beside it -->
        <div
            class="min-w-0 text-xs"
            title="Decoded only while shown: every 2 s from its key frames, or live (a full decode on the node)"
        >
            <PreviewToggle />
            <!-- an encode's newest key frame at full size: a passthrough's is its input's stream -->
            <button
                v-if="kind === 'encodes'"
                class="mt-1 block text-xs"
                title="Full size, its newest key frame, decoded from what it sends"
                @click="openFrame"
            >
                Grab frame (PNG)
            </button>
        </div>
    </div>
</template>
